import { X } from "@icons";
import * as Dialog from "@radix-ui/react-dialog";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { useCleanupAfterRadix } from "../../hooks/helperHooks";

export type CloseButtonVariant = "rennala" | "legacy";

const CLOSE_BUTTON_VARIANT_CLASSES: Record<CloseButtonVariant, string> = {
  rennala:
    "text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-300",
  legacy: "",
};

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  openTriggerChild?: React.ReactNode;
  triggerAsChild?: boolean;
  closeButtonVariant?: CloseButtonVariant;
  size?: "sm" | "md" | "lg";
  hideTitle?: boolean;
  hideDescription?: boolean;
}

export default function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  openTriggerChild,
  triggerAsChild = false,
  closeButtonVariant = "rennala",
  size = "md",
  hideTitle = false,
  hideDescription = false,
}: ModalProps) {
  const { t } = useTranslation();

  useCleanupAfterRadix(open);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {openTriggerChild && (
        <Dialog.Trigger className="rounded" asChild={triggerAsChild}>
          {openTriggerChild}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black bg-opacity-50" />
        <Dialog.Content
          className={classNames(
            "fixed z-[60] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-md max-h-[80%] w-[90%] flex flex-col",
            {
              "max-w-[400px]": size === "sm",
              "max-w-[600px]": size === "md",
              "max-w-[800px]": size === "lg",
            },
          )}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex-shrink-0 p-4 pb-0">
            <Dialog.Title
              className={classNames(
                "font-semibold text-xl my-2 pr-11 break-words text-primary-500",
                {
                  "sr-only": hideTitle,
                },
              )}
            >
              {title}
            </Dialog.Title>
            <Dialog.Description
              className={classNames("text-gray-700 mb-4", {
                "sr-only": hideDescription,
              })}
            >
              {description}
            </Dialog.Description>
          </div>
          <div className="flex-1 overflow-auto p-4 pt-0">{children}</div>
          <Dialog.Close
            aria-label={t("close")}
            className={classNames(
              "absolute top-3 right-3 rounded-full w-8 h-8 flex justify-center items-center bg-white",
              CLOSE_BUTTON_VARIANT_CLASSES[closeButtonVariant],
            )}
          >
            <X size={18} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
